import React, { PropsWithChildren, useMemo } from 'react';

import PropTypes from 'prop-types';
import { CSSObject } from 'styled-components';

import { stripUnknownProps } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { TABLE_SIZES } from '../consts';
import useTableSize from '../hooks/useTableSize';
import { Themeable } from '../types';

type Cell = {
  size?: number | string;
  isResizing?: boolean;
  enableRowSelection?: boolean;
};

export type TableCellProps = StylableComponent<HTMLDivElement> &
  Themeable &
  Cell;

const unknownProps = ['columnId'];

const TableCell = ({
  size,
  isResizing,
  enableRowSelection,
  children,
  tableSize,
  ...props
}: PropsWithChildren<TableCellProps>) => {
  const tableSizeTheme = useTableSize(tableSize);
  const theme = useTheme();
  const cellTokens = theme.tokens?.component?.table?.cell;
  const tdProps = stripUnknownProps(props, unknownProps);

  const cellCss = useMemo(
    (): CSSObject => ({
      width: typeof size === 'number' ? `${size}px` : size,
      flex: `${size} 0 auto`,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      padding: `0 ${tableSizeTheme.cellPadding}px`,
      ...cellTokens?._,
      borderRightWidth: '1px',
      borderRightStyle: 'solid',
      borderRightColor: isResizing
        ? cellTokens?.resizing?.['border-right-color']
        : 'transparent',
      '& a': {
        width: typeof size === 'number' ? `${size}px` : size,
        flex: `${size} 0 auto`,
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        ...(enableRowSelection
          ? { textDecoration: 'none', color: 'inherit' }
          : {}),
      },
    }),

    [
      size,
      tableSizeTheme.cellPadding,
      cellTokens,
      isResizing,
      enableRowSelection,
    ],
  );

  return (
    <td
      // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
      role="cell"
      tabIndex={-1}
      {...tdProps}
      css={cellCss}
    >
      {children}
    </td>
  );
};

TableCell.propTypes = {
  tableSize: PropTypes.oneOf(Object.values(TABLE_SIZES)),
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isResizing: PropTypes.bool,
  enableRowSelection: PropTypes.bool,
  children: PropTypes.any,
  style: stylePropType,
};

export default TableCell;
